import React, { useEffect, useState } from "react";
import { Input, Modal, Checkbox, Button, message } from "antd";
import { buttonStyleNegative, buttonStylePositive } from "../../../services/food-service";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import { Link } from "react-router-dom";
import CustomInputNumberV2 from "../../CustomInputNumberV2";
import { CharLimit as charLimit } from "../../../constant/constants";

const FoodMealAddition = ({ foodAdditionGroups, setFoodAdditionGroups }) => {
  const { user, restaurantId, inventoryType } = useUserStore();
  const [mealList, setMealList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
  const [tempSelectedItems, setTempSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const userToken = user?.token?.token;

  // Fetch meal additions list
  const fetchMealList = async () => {
    if (!restaurantId) {
      message.error("Restaurant ID is required");
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.GET_ADDITION_ITEM_LIST(restaurantId, userToken);

      if (response?.code !== 200) {
        message.error(response?.message || "Failed to fetch meal additions");
        return;
      }

      setMealList(response.payload.records || []);
    } catch (error) {
      console.error("Error fetching meal list:", error);
      message.error(error?.response?.data?.message || "An error occurred while fetching meal additions");
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchMealList();
  }, [restaurantId]);

  // Add new group safely
  const addGroup = () => {
    setFoodAdditionGroups((prev) => ({
      additionGroups: [
        ...(prev.additionGroups || []),
        {
          additionGroupId: "",
          name: "",
          numberOfFreeItems: 0,
          pricePerAddition: 0,
          additionItems: [],
        },
      ],
    }));
  };

  // Delete group by index safely
  const deleteGroup = (index) => {
    setFoodAdditionGroups((prev) => {
      const groups = Array.isArray(prev?.additionGroups) ? [...prev.additionGroups] : [];
      groups.splice(index, 1);
      return { additionGroups: groups };
    });
  };

  // Open modal to select addition items for a group
  const openModal = (index) => {
    setCurrentGroupIndex(index);
    const currentGroup = foodAdditionGroups?.additionGroups?.[index];
    if (currentGroup) {
      setTempSelectedItems(currentGroup.additionItems.map((item) => item.additionItemId) || []);
    } else {
      setTempSelectedItems([]);
    }
    setModalVisible(true);
  };

  // Save modal selections back to the group
  const handleSaveModal = () => {
    if (tempSelectedItems.length > 15) {
      message.warning("You cannot select more than 15 items in a group.");
      return;
    }

    setFoodAdditionGroups((prev) => {
      const groups = Array.isArray(prev?.additionGroups) ? [...prev.additionGroups] : [];
      if (groups[currentGroupIndex]) {
        groups[currentGroupIndex].additionItems = tempSelectedItems.map((id) => {
          const item = mealList.find((i) => i.additionItemId === id);
          return { additionItemId: id, name: item?.name || "" };
        });
      }
      return { additionGroups: groups };
    });
    setModalVisible(false);
    setTempSelectedItems([]);
  };

  // Cancel modal without saving
  const handleCancelModal = () => {
    setModalVisible(false);
    setTempSelectedItems([]);
  };

  // Update a field in a group
  const updateGroupField = (index, field, value) => {
    // Enforce character limit for 'name' field
    if (value.length > charLimit.Name) return;

    setFoodAdditionGroups((prev) => {
      const groups = Array.isArray(prev?.additionGroups) ? [...prev.additionGroups] : [];
      if (groups[index]) {
        groups[index][field] = value;
      }
      return { additionGroups: groups };
    });
  };

  const onChangeGroupInput = (nameValObj) => {
    const [index, field] = nameValObj.name.split("_");
    updateGroupField(index, field, nameValObj.value);
  };
  // Checkbox group change handler
  const handleCheckboxChange = (checkedValues) => {
    setTempSelectedItems(checkedValues);
  };

  // Defensive render in case foodAdditionGroups is not set properly
  const groups = Array.isArray(foodAdditionGroups?.additionGroups) ? foodAdditionGroups.additionGroups : [];

  // Collect all selected item IDs from other groups
  const getDisabledItems = () => {
    const allSelected = new Set();
    groups.forEach((group, idx) => {
      if (idx !== currentGroupIndex) {
        group.additionItems?.forEach((item) => allSelected.add(item.additionItemId));
      }
    });
    return allSelected;
  };
  const disabledItems = getDisabledItems();

  return (
    <div className="py-6 space-y-4">
      <div className="flex items-center mb-4">
        <h3 className="text-gray-700 font-semibold whitespace-nowrap mr-4">Meal Addition</h3>
        <div className="flex-1 border-t border-gray-300"></div>
        <img src="/images/add-icon.svg" alt="Add" className="w-8 h-8 ml-4 cursor-pointer" onClick={addGroup} />
      </div>

      {loading ? (
        <div className="h-[20vh] flex items-center justify-center">Loading...</div>
      ) : (
        groups.map((group, index) => (
          <div key={index} className="border p-4 rounded shadow-sm">
            <div className="flex   gap-4">
              <div className="flex-1 min-w-[120px]">
                <Input
                  placeholder="Name"
                  value={group.name}
                  onChange={(e) => updateGroupField(index, "name", e.target.value)}
                  className="w-full"
                />
                <div className="mt-1 text-[12px] text-gray-600 italic">
                  Char limit 100 ({group.name.length})
                  {/*Name must be 100 characters or less. Current: {group.name.length}*/}
                </div>
              </div>
              <div className="w-[230px]">
                <CustomInputNumberV2
                  min={0}
                  max={10}
                  addonBefore="Number of Foods [Free]"
                  name={`${index}_numberOfFreeItems`}
                  value={group.numberOfFreeItems}
                  setValue={onChangeGroupInput}
                  shouldSetValueSetObj={true}
                />
              </div>
              <div className="w-[230px]">
                <CustomInputNumberV2
                  min={0}
                  max={99999}
                  addonBefore="Price after free item"
                  name={`${index}_pricePerAddition`}
                  value={group.pricePerAddition}
                  setValue={onChangeGroupInput}
                  shouldSetValueSetObj={true}
                  isDecimalAllowed={true}
                  inputClasses="flex-1"
                />
              </div>
              <Button type="primary" style={buttonStylePositive} onClick={() => openModal(index)}>
                Item +
              </Button>
              <Button type="text" className="p-0" onClick={() => deleteGroup(index)}>
                <img src="/images/deleteicon.svg" alt="Remove" className="w-8 h-8 cursor-pointer" />
              </Button>
            </div>

            {/* Selected Items List */}
            {group.additionItems && group.additionItems.length > 0 && (
              <div className="w-full mt-2 text-sm text-gray-700 space-y-2">
                {group.additionItems.map((item) => (
                  <div key={item.additionItemId} className="flex items-center gap-2 w-full">
                    <img src="/images/circle-list-icon.svg" alt="list" className="w-4 h-4" />
                    <span className="font-semibold break-words whitespace-normal flex-1 min-w-0">
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {/* Modal */}
      <Modal open={modalVisible} footer={null} closable={false} onCancel={handleCancelModal} width={400} centered>
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h3 className="text-sm font-semibold">ITEMS</h3>
          <button onClick={handleCancelModal} className="text-xl font-bold text-gray-500 hover:text-black">
            ×
          </button>
        </div>

        {mealList.length > 0 ? (
          <>
            <div className="mb-1 text-[12px] text-gray-600 italic">
              You must select at least 1 and no more than 15 items
            </div>
            <Checkbox.Group
              className="w-full flex flex-col space-y-2 break-words break-all whitespace-normal"
              options={mealList.map((item) => ({
                label: item.name,
                value: item.additionItemId,
                disabled: disabledItems.has(item.additionItemId), // Disable if already selected in other groups
              }))}
              value={tempSelectedItems}
              onChange={handleCheckboxChange}
            />

            <div className="flex justify-center space-x-4 mt-6">
              <Button type="primary" style={buttonStyleNegative} onClick={handleCancelModal}>
                Cancel
              </Button>

              <Button type="primary" style={buttonStylePositive} onClick={handleSaveModal}>
                Save
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-600 text-sm mt-4">
            You don't have any additional meal items.
            {inventoryType === "ITEM" && (
              <>
                For adding items, please{" "}
                <Link to="/add-meal-addition" className="text-blue-500 underline">
                  click here
                </Link>
                .
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FoodMealAddition;
